import { log } from '@/log'
import { HlAppMode } from '@/models/hl-app/hl-app-mode'
import { SessionApiService } from '@/services/api/session'
import { SessionApiErrorResponse } from '@/services/api/session/response'
import { SessionCookie, SessionCookiePayload } from '@/services/session/session-cookie'

const namespace = 'sync-session'

/**
 * 正しいセッションがcookieに存在する状態に同期する処理
 * - cookieが存在する
 * - cookieの情報がSSOT（技術的にはRedisのレコード）と等しい
 *
 * ただし、重複実行を避けるために成功結果をキャッシュしている
 * 現在のcookieが同期成功時と異なる場合はキャッシュを破棄して再実行する
 */
export class SyncSession {
  static exec(): Promise<void> {
    return SyncSession.getInstance().exec()
  }

  static onSynced<T>(callback: () => Promise<T>): Promise<T> {
    return SyncSession.getInstance().onSynced(callback)
  }

  static getInstance(): SyncSession {
    if (!SyncSession.instance) {
      SyncSession.instance = new SyncSession()
    }

    return SyncSession.instance
  }

  private static instance: SyncSession

  private syncPromise: Promise<void> | null = null
  private syncedSessionCacheKey: string | null = null

  private constructor(
    private readonly sessionApiService: SessionApiService = new SessionApiService(),
    private readonly sessionCookie: SessionCookie = new SessionCookie()
  ) {}

  exec(): Promise<void> {
    this.invalidateCacheIfCookieChanged()

    if (this.syncPromise) {
      return this.syncPromise
    }

    this.syncPromise = this.innerSync()
      .then((payload) => {
        this.syncedSessionCacheKey = this.toSessionCacheKey(payload)
        log.debug({ service: `${namespace}/sync`, payload })

        return Promise.resolve()
      })

    return this.syncPromise
  }

  onSynced<T>(callback: () => Promise<T>): Promise<T> {
    return this.exec().then(callback)
  }

  /**
   * - セッションcookieが存在する（client側でパースできる範囲で正しいと思われる）
   *   - keepCurrentする
   *   - keepCurrentが正常に完了すれば成功
   *   - keepCurrentがセッション不正のエラーを返した場合、セッションが存在しないルートを実行する
   *   - keepCurrentがそれ以外のエラーを返した場合、Promiseをrejectする
   * - セッションcookieが存在しない
   *   - newする
   *   - newが正常に完了すれば成功
   *   - newが正常に完了しなければPromiseをrejectする
   */
  private async innerSync(): Promise<SessionCookiePayload> {
    const maybePayload = this.sessionCookie.readPayload()
    if (maybePayload) {
      const keepResult = await this.sessionApiService.keepCurrent()

      return keepResult.fold(
        (error: SessionApiErrorResponse) => {
          if (error.errorCode === 'invalid_cookie' || error.errorCode === 'not_found') {
            return this.new()
          }

          throw new Error(`${error.errorCode}: ${error.message}`)
        },
        () => {
          // keepCurrentに成功したのでcookieは存在するはず
          // 存在しなければrejectする
          return Promise.resolve(this.sessionCookie.readPayloadOrFail())
        }
      )
    }

    return this.new()
  }

  private async new(): Promise<SessionCookiePayload> {
    const isHlApp = HlAppMode.isPageForHlApp || HlAppMode.isViewedInHlAppWebView
    const result = await this.sessionApiService.new({
      isHlApp,
    })

    return result.fold(
      (error: SessionApiErrorResponse) => {
        throw new Error(`${error.errorCode}: ${error.message}`)
      },
      // newに成功したのでcookieは存在するはず
      // 存在しなければrejectする
      () => Promise.resolve(this.sessionCookie.readPayloadOrFail())
    )
  }

  /**
   * 前回のsync完了時のcookieと現在のcookieで状態が異なっている場合、
   * syncPromiseのキャッシュを破棄し再実行可能な状態に遷移する。
   * cookieの同一性はpayloadから作成されるcache keyで判定する。
   */
  private invalidateCacheIfCookieChanged(): void {
    if (!this.syncPromise || !this.syncedSessionCacheKey) {
      return
    }

    const currentPayload = this.sessionCookie.readPayload()
    if (currentPayload && this.toSessionCacheKey(currentPayload) === this.syncedSessionCacheKey) {
      return
    }

    this.syncPromise = null
    this.syncedSessionCacheKey = null
  }

  private toSessionCacheKey(payload: SessionCookiePayload): string {
    return `${payload.id}:${payload.v}:${payload.startAt}`
  }
}
